import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import axiosInstance from '../../../api/AxiosInstance';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  evento: any;
  onGuardado: () => void;
}

const getBaseOrigin = () => {
  const configured = axiosInstance.defaults.baseURL as string | undefined;
  if (!configured) return window.location.origin;
  try {
    return new URL(configured, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
};

const resolveAssetUrl = (value: string) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${getBaseOrigin()}${value.startsWith('/') ? '' : '/'}${value}`;
};

const ModalEvidenciasTurismo: React.FC<Props> = ({ isOpen, onClose, evento, onGuardado }) => {
  const [form, setForm] = useState<any>({
    visitantes: '',
    derrama: '',
    obsFinales: '',
  });
  const [evidenciasFotosExistentes, setEvidenciasFotosExistentes] = useState<string[]>([]);
  const [evidenciasDocsExistentes, setEvidenciasDocsExistentes] = useState<string[]>([]);
  const [evidenciasFotosNuevas, setEvidenciasFotosNuevas] = useState<File[]>([]);
  const [evidenciasDocsNuevos, setEvidenciasDocsNuevos] = useState<File[]>([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (evento) {
      setForm({
        visitantes: evento.visitantes ?? '',
        derrama: evento.derrama ?? '',
        obsFinales: evento.obsFinales || '',
      });
      setEvidenciasFotosExistentes(Array.isArray(evento.evidenciasFotos) ? evento.evidenciasFotos.filter(Boolean) : []);
      setEvidenciasDocsExistentes(Array.isArray(evento.evidenciasDocs) ? evento.evidenciasDocs.filter(Boolean) : []);
      setEvidenciasFotosNuevas([]);
      setEvidenciasDocsNuevos([]);
    }
  }, [evento]);

  const handleChange = (field: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setGuardando(true);
    try {
      const fd = new FormData();
      fd.append('visitantes', String(form.visitantes ?? ''));
      fd.append('derrama', String(form.derrama ?? ''));
      fd.append('obsFinales', String(form.obsFinales ?? ''));
      fd.append('evidenciasFotosExistentes', JSON.stringify(evidenciasFotosExistentes));
      fd.append('evidenciasDocsExistentes', JSON.stringify(evidenciasDocsExistentes));
      evidenciasFotosNuevas.forEach((f) => fd.append('evidenciasFotos', f));
      evidenciasDocsNuevos.forEach((f) => fd.append('evidenciasDocs', f));

      await axiosInstance.post(`/turismo/eventos/${evento.id}/evidencias`, fd);
      onGuardado();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="xl" overflow="hidden">
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b" bg="gray.50">
          Evidencias del evento
        </ModalHeader>
        <ModalBody py={5} bg="white">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>Fotos de evidencia (multimedia)</FormLabel>
              <Input type="file" accept="image/*" multiple onChange={(e) => setEvidenciasFotosNuevas((prev) => [...prev, ...Array.from(e.target.files || [])])} p={1} />
              <FormHelperText>Sube fotos, no URLs.</FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>Documentos de evidencia (multimedia)</FormLabel>
              <Input type="file" accept=".pdf,image/*" multiple onChange={(e) => setEvidenciasDocsNuevos((prev) => [...prev, ...Array.from(e.target.files || [])])} p={1} />
              <FormHelperText>Sube PDF/imagen, no URLs.</FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>Visitantes</FormLabel>
              <Input type="number" value={form.visitantes} onChange={(e) => handleChange('visitantes', e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>Derrama</FormLabel>
              <Input type="number" value={form.derrama} onChange={(e) => handleChange('derrama', e.target.value)} />
            </FormControl>
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Observaciones finales</FormLabel>
              <Textarea value={form.obsFinales} onChange={(e) => handleChange('obsFinales', e.target.value)} />
            </FormControl>
          </SimpleGrid>

          <Box mt={5}>
            <Text fontWeight="semibold" mb={2}>Fotos registradas</Text>
            {evidenciasFotosExistentes.length === 0 ? (
              <Text fontSize="sm" color="gray.500">Sin evidencias fotográficas registradas.</Text>
            ) : (
              <Wrap>
                {evidenciasFotosExistentes.map((item) => (
                  <WrapItem key={item}>
                    <Tag colorScheme="red" borderRadius="full">
                      <TagLabel><Link href={resolveAssetUrl(item)} isExternal>{item.split('/').pop()}</Link></TagLabel>
                      <TagCloseButton onClick={() => setEvidenciasFotosExistentes((prev) => prev.filter((x) => x !== item))} />
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Box>

          <Box mt={4}>
            <Text fontWeight="semibold" mb={2}>Documentos registrados</Text>
            {evidenciasDocsExistentes.length === 0 ? (
              <Text fontSize="sm" color="gray.500">Sin documentos registrados.</Text>
            ) : (
              <Wrap>
                {evidenciasDocsExistentes.map((item) => (
                  <WrapItem key={item}>
                    <Tag colorScheme="purple" borderRadius="full">
                      <TagLabel><Link href={resolveAssetUrl(item)} isExternal>{item.split('/').pop()}</Link></TagLabel>
                      <TagCloseButton onClick={() => setEvidenciasDocsExistentes((prev) => prev.filter((x) => x !== item))} />
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Box>

          <Box mt={4}>
            <Text fontWeight="semibold" mb={2}>Nuevas fotos por subir</Text>
            {evidenciasFotosNuevas.length === 0 ? (
              <Text fontSize="sm" color="gray.500">No has seleccionado fotos.</Text>
            ) : (
              <Wrap>
                {evidenciasFotosNuevas.map((f, i) => (
                  <WrapItem key={`${f.name}-${i}`}>
                    <Tag colorScheme="blue" borderRadius="full">
                      <TagLabel>{f.name}</TagLabel>
                      <TagCloseButton onClick={() => setEvidenciasFotosNuevas((prev) => prev.filter((_, idx) => idx !== i))} />
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Box>

          <Box mt={4}>
            <Text fontWeight="semibold" mb={2}>Nuevos documentos por subir</Text>
            {evidenciasDocsNuevos.length === 0 ? (
              <Text fontSize="sm" color="gray.500">No has seleccionado documentos.</Text>
            ) : (
              <Wrap>
                {evidenciasDocsNuevos.map((f, i) => (
                  <WrapItem key={`${f.name}-${i}`}>
                    <Tag colorScheme="green" borderRadius="full">
                      <TagLabel>{f.name}</TagLabel>
                      <TagCloseButton onClick={() => setEvidenciasDocsNuevos((prev) => prev.filter((_, idx) => idx !== i))} />
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Box>
        </ModalBody>
        <ModalFooter gap={2} borderTop="1px solid" borderColor="gray.100" bg="gray.50">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleSubmit} isLoading={guardando} loadingText="Guardando">Guardar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalEvidenciasTurismo;
